import { useQuery } from "@apollo/client";
import CompanyIssuersQuery from "components/financials/modern/graphql/CompanyIssuersQuery";
import type {
  ICompanyIssuersQuery,
  ICompanyIssuersQueryVariables,
} from "components/financials/modern/graphql/__generated__/CompanyIssuersQuery.generated";
import type { UrlSafeIssuerRef } from "components/issuers/utils";
import { deduplicateSelectedIssuersList, encodeIssuerRefToUrl } from "components/issuers/utils";
import notUndefinedOrNull from "lib/utils/notUndefinedOrNull";
import { useEffect } from "react";
import { useIntl } from "react-intl";
import type { ICompanyIssuerSearchRefInput } from "types/graphqlTypes";

export const cache = new Map<string, ICompanyIssuersQuery["companyIssuers"][0]>();
const isServer = typeof window === "undefined";

export default function useCompanyIssuers(input: ICompanyIssuerSearchRefInput[]) {
  const intl = useIntl();
  useEffect(() => {
    // Damm, we need to clear the cache when the locale changes
    cache.clear();
  }, [intl.locale]);

  const missingRefs = input.filter((ref) => {
    const key = encodeIssuerRefToUrl(ref);
    if (key === null) {
      return false;
    }
    return !cache.has(key);
  });

  const { data, previousData, loading, ...rest } = useQuery<ICompanyIssuersQuery, ICompanyIssuersQueryVariables>(
    CompanyIssuersQuery,
    {
      notifyOnNetworkStatusChange: true,
      fetchPolicy: "cache-first",
      variables: {
        input: isServer ? input : missingRefs,
        descriptionLanguage: intl.locale.substring(0, 2),
      },
    },
  );

  if (loading && previousData?.companyIssuers) {
    return {
      ...rest,
      loading,
      issuers: deduplicateSelectedIssuersList(previousData?.companyIssuers),
    };
  }

  const hits = input
    .map((ref) => {
      const key = encodeIssuerRefToUrl(ref);
      return key ? cache.get(key) : null;
    })
    .filter(notUndefinedOrNull);

  const fullMatch = input.every((ref) => {
    return cache.has(encodeIssuerRefToUrl(ref) || "");
  });

  // If we have all the refs in cache, we can return the hits
  if (!isServer && missingRefs.length === 0 && fullMatch) {
    return {
      ...rest,
      loading,
      issuers: deduplicateSelectedIssuersList(hits),
    };
  }

  const issuers = (!data && previousData?.companyIssuers ? previousData?.companyIssuers : data?.companyIssuers) || [];
  issuers.forEach((hit) => {
    const refs = [
      {
        tickerSymbol: hit.tickerSymbol,
        country: hit.country,
      },
      {
        tickerSymbol: hit.tickerSymbol,
        exchangeId: hit.exchangeId,
      },
      {
        tickerSymbol: hit.tickerSymbol,
        vertical: hit.vertical,
      },
      {
        tickerSymbol: hit.tickerSymbol,
      },
    ] as UrlSafeIssuerRef[];
    refs.forEach((ref) => {
      const key = encodeIssuerRefToUrl(ref);
      if (key) {
        cache.set(key, hit);
      }
    });
  });

  return {
    ...rest,
    loading,
    issuers: deduplicateSelectedIssuersList(
      input
        .map((input) => {
          const key = encodeIssuerRefToUrl(input);
          return key ? cache.get(key) : null;
        })
        .filter(notUndefinedOrNull),
    ),
  };
}

export function useSelectedCompanyIssuer(input: ICompanyIssuerSearchRefInput | null) {
  const { issuers, ...rest } = useCompanyIssuers(input ? [input] : []);
  const issuer = issuers.length > 0 ? issuers[0] : null;
  return {
    ...rest,
    issuer,
  };
}
